/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { MultilingualStatusType } from '../modules/ia/store/multilingual/helper';
import type { ChannelType } from './channel';
import type { Language, SkillReferenceType, SkillType } from './skill';
import type { CustomRole, Role } from '.';

export type Assistant = {
  id?: string;
  agent_instance_id?: string;
  assistant_id: string;
  created: string;
  custom_roles: CustomRole[];
  description: string;
  environment: string;
  language: Language;
  certificates: Certificate[];
  certification_method: string;
  name: string;
  updated: string;
  orchestration: {
    search_skill_fallback: boolean;
  };
  integration_references: IntegrationReference[];
  linked_assistant: LinkedAssistant[];
  role: Role;
  // New IA assistants will instead contain "session_history" in "assistant_environments"
  session_history?: SessionHistory;
  session_timeout: number;
  // Classic IA assistants will contain "skill_references".
  skill_references?: SkillReference[];

  // New IA assistants will instead contain "assistant_skills".
  assistant_skills?: SkillReference[];
  assistant_environments?: EnvironmentBase[];
  multi_lingual?: Multilingual;
};

export type LinkedAssistant = {
  assistant_id: string;
  name: string;
};

type Certificate = {
  content: string;
};

export type SessionHistory = {
  enabled: boolean;
  max_recent_items_count?: number;
  max_item_size?: number;
  max_total_size?: number;
  role_user?: string;
  role_assistant?: string;
};

export type SkillReference = {
  created?: string;
  disabled?: boolean;
  skill_id: string;
  skill_reference?: SkillReferenceType;
  snapshot?: string;
  type: SkillType;
  updated?: string;
};

export type IntegrationReference = {
  type: ChannelType;
  integration_id: string;
};

export const enum EnvironmentType {
  DRAFT = 'draft',
  LIVE = 'live',
  STAGING = 'staging'
}
export interface EnvironmentBase {
  environment_id: string;
  environment: EnvironmentType;
  name: string;
}

export interface Environment extends EnvironmentBase {
  assistant_id: string;
  created: string;
  custom_roles: CustomRole[];
  description: string;
  integration_references: IntegrationReference[];
  language: Language;
  role: Role;
  session_history?: SessionHistory;
  session_timeout: number;
  skill_references: SkillReference[];
  updated: string;
}

export type Multilingual = {
  status?: MultilingualStatusType;
  created?: string;
  enabled?: boolean;
  updated?: string;
  assistant_id?: string;
  release?: string;
  code?: string;
  errors?: MultilingualStatusError[];
  status_errors?: MultilingualStatusError[];
};

export type MultilingualStatusError = {
  message?: string;
};

export enum SSL_SELECTION {
  UPLOAD_ANY = 'inclusive',
  UPLOAD = 'exclusive',
  All = 'unauthorized'
}
